import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { StorageKeys, StorageService } from '../services/storage.service';
import { Enterprise } from '../types/enterprise';
import { Facility } from '../types/facility';
import { UserRoleStore } from './user-role.store';

@Injectable({
  providedIn: 'root',
})
export class EnterpriseStore {
  private currentEnterpriseId: string | null = this.storageService.getItem(StorageKeys.CURRENT_ENTERPRISE_ID);
  private currentEnterpriseSubj$: BehaviorSubject<Enterprise> = new BehaviorSubject<Enterprise>(null!);

  currentEnterprise$ = this.currentEnterpriseSubj$.asObservable();
  enterprises: Enterprise[] = [];

  constructor(
    private storageService: StorageService,
    private userRoleStore: UserRoleStore,
  ) {}

  getEnterpriseFacilities(enterpriseId: string) {
    return this.enterprises.find((ent) => ent.id === enterpriseId)?.facilities ?? [];
  }

  setCurrentEnterprise(enterprise: Enterprise): void {
    if (enterprise) {
      this.currentEnterpriseId = enterprise.id;
      this.storageService.setItem(StorageKeys.CURRENT_ENTERPRISE_ID, enterprise.id);
    } else {
      this.currentEnterpriseId = null;
      this.storageService.removeItem(StorageKeys.CURRENT_ENTERPRISE_ID);
    }

    this.userRoleStore.setCurrentEnterpriseId(this.currentEnterpriseId);
    this.currentEnterpriseSubj$.next(enterprise);
  }

  getEnterpriseById(enterpriseId: string): Enterprise | undefined {
    return this.enterprises.find((ent) => ent.id === enterpriseId);
  }

  canManuallyManageEmployees(enterpriseId?: string | null): boolean {
    const targetId = enterpriseId ?? this.getCurrentEnterpriseId();
    const current = this.getCurrentEnterprise();
    const enterprise = current?.id === targetId ? current : this.getEnterpriseById(targetId ?? '');
    return !!enterprise && !enterprise.dataSyncEnabled;
  }

  getCurrentEnterprise(): Enterprise {
    return this.currentEnterpriseSubj$.value;
  }

  getCurrentEnterpriseId(): string | null {
    return this.currentEnterpriseId;
  }

  getCurrentEnterpriseFacilities(): Facility[] {
    return this.getEnterpriseFacilities(this.currentEnterpriseId!);
  }

  clear(): void {
    this.storageService.removeItem(StorageKeys.CURRENT_ENTERPRISE_ID);
    this.currentEnterpriseSubj$.next(null!);
  }

  enterpriseRenamed(enterpriseId: string, enterpriseName: string) {
    let currentEnterprise = this.getCurrentEnterprise();
    if (currentEnterprise?.id === enterpriseId) {
      currentEnterprise.name = enterpriseName;
    }
  }

  facilityRenamed(facilityId: string, facilityName: string) {
    this.enterprises.forEach((enterprise) => {
      enterprise.facilities?.forEach((facility) => {
        if (facility.id === facilityId) {
          facility.name = facilityName;
        }
      });
    });
  }

  facilityRemoved(facilityId: string) {
    this.enterprises.forEach((enterprise) => {
      enterprise.facilities = enterprise.facilities?.filter((facility) => facility.id !== facilityId);
    });
  }
}
